import type { ReactNode } from "react";

type TileStatus = "connected" | "paused" | "disconnected" | "loading";

type Props = {
  name: string;
  description: string;
  logo: ReactNode;
  status: TileStatus;
  statusLabel: string;
  onClick: () => void;
  disabled?: boolean;
  footer?: ReactNode; // separate interactive area (e.g. pause toggle)
};

export function IntegrationTile({
  name,
  description,
  logo,
  status,
  statusLabel,
  onClick,
  disabled = false,
  footer,
}: Props) {
  return (
    <div className="group flex h-full flex-col overflow-hidden rounded-2xl border border-[color:var(--border-light)] bg-[color:var(--surface)] transition hover:border-[color:var(--primary-300)] hover:shadow-sm">
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className="flex flex-1 flex-col gap-4 p-5 text-start disabled:cursor-not-allowed disabled:opacity-60"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex h-8 items-center">{logo}</div>
          <StatusChip status={status} label={statusLabel} />
        </div>
        <div className="flex flex-col gap-1">
          <p className="text-sm font-semibold text-[color:var(--text-primary)]">
            {name}
          </p>
          <p className="text-xs leading-snug text-[color:var(--text-muted)]">
            {description}
          </p>
        </div>
      </button>
      {footer ? (
        <div className="flex items-center justify-between gap-3 border-t border-[color:var(--border-light)] px-5 py-3">
          {footer}
        </div>
      ) : null}
    </div>
  );
}

function StatusChip({ status, label }: { status: TileStatus; label: string }) {
  if (status === "connected") {
    return (
      <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-[color:var(--success-50,#E8F7EE)] px-2 py-0.5 text-[11px] font-semibold text-[color:var(--success-700,#116733)]">
        <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--success-500,#16A34A)]" />
        {label}
      </span>
    );
  }
  if (status === "paused") {
    return (
      <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
        {label}
      </span>
    );
  }
  if (status === "loading") {
    return (
      <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-[color:var(--surface-alt)] px-2 py-0.5 text-[11px] font-semibold text-[color:var(--text-muted)]">
        •••
      </span>
    );
  }
  return (
    <span className="inline-flex shrink-0 items-center rounded-full border border-[color:var(--border-light)] bg-[color:var(--surface)] px-2 py-0.5 text-[11px] font-semibold text-[color:var(--text-secondary)]">
      {label}
    </span>
  );
}
